import React from 'react';
import { SET_CATEGORIES, getMinExercisesForCategory, getMaxExercisesForCategory } from '../../constants/blockTypes';
import { colors as theme } from '../../theme';

const brandColors = {
  gold: theme.gold,
  darkGray: theme.surface1,
  mediumGray: theme.surface2,
  lightGray: theme.surface3,
  silver: theme.textMuted,
  white: theme.text,
};

/**
 * Block Type Selector Component
 *
 * Dropdown to select the set category for a block.
 * Shows exercise count requirements and category description.
 *
 * Props:
 * - value: Current set_category value
 * - onChange: Callback when category changes
 * - currentExerciseCount: Number of exercises currently in the block
 * - disabled: Boolean to disable selection
 */
const BlockTypeSelector = ({
  value = 'normal',
  onChange,
  currentExerciseCount = 0,
  disabled = false,
}) => {
  const categories = Object.values(SET_CATEGORIES);
  const currentCategory = SET_CATEGORIES[value] || SET_CATEGORIES.normal;

  const handleChange = (e) => {
    const newCategory = e.target.value;
    onChange(newCategory);
  };

  // Check if category is valid for current exercise count
  const isValidForCount = (categoryId) => {
    const minEx = getMinExercisesForCategory(categoryId);
    const maxEx = getMaxExercisesForCategory(categoryId);
    return currentExerciseCount >= minEx && currentExerciseCount <= maxEx;
  };

  return (
    <div style={styles.container}>
      <label style={styles.label}>Set Category</label>
      <select
        value={value}
        onChange={handleChange}
        disabled={disabled}
        style={{
          ...styles.select,
          borderColor: value !== 'normal' ? brandColors.gold : brandColors.lightGray,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {categories.map((cat) => {
          const minEx = getMinExercisesForCategory(cat.id);
          const maxEx = getMaxExercisesForCategory(cat.id);
          const countLabel = minEx === maxEx ? `${minEx} exercises` : `${minEx}-${maxEx} exercises`;

          return (
            <option key={cat.id} value={cat.id}>
              {cat.label} ({countLabel})
            </option>
          );
        })}
      </select>

      {/* Description tooltip */}
      <div style={styles.description}>
        {currentCategory.description}
        {currentExerciseCount > 0 && !isValidForCount(value) && (
          <span style={styles.warning}>
            {' '}Requires {getMinExercisesForCategory(value)}-{getMaxExercisesForCategory(value)} exercises
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Compact category badge for display
 */
export const CategoryBadge = ({ category, params }) => {
  const cat = SET_CATEGORIES[category];
  if (!cat || category === 'normal') return null;

  let label = cat.label;

  // Add param info for certain categories
  if (category === 'drop_set' && params?.dropsCount) {
    label = `Drop Set (${params.dropsCount})`;
  } else if (category === 'negative' && params?.eccentricSeconds) {
    label = `Negative (${params.eccentricSeconds}s)`;
  }

  return (
    <span style={styles.badge}>
      {label}
    </span>
  );
};

const styles = {
  container: {
    marginBottom: '12px',
  },
  label: {
    display: 'block',
    color: brandColors.silver,
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    marginBottom: '6px',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: brandColors.darkGray,
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '8px',
    color: brandColors.white,
    fontSize: '14px',
    outline: 'none',
    cursor: 'pointer',
  },
  description: {
    marginTop: '6px',
    color: brandColors.silver,
    fontSize: '12px',
    lineHeight: '1.4',
  },
  warning: {
    color: theme.warning,
    fontWeight: '600',
  },
  badge: {
    display: 'inline-block',
    padding: '3px 8px',
    backgroundColor: brandColors.gold,
    color: brandColors.darkGray,
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: '700',
    marginRight: '6px',
  },
};

export default BlockTypeSelector;
